// attendanceSlice.js - UPDATED for simplified backend structure
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchLiveAttendance,
  fetchTodaySummary,
  fetchMyAttendance,
  fetchEmployeeSummary,
  updateAttendanceStatus,
  updateAttendancePunches,
  createAttendance,
  deleteAttendance,
  bulkUpdateAttendance,
} from './attendanceThunks';

const initialState = {
  // Today's Summary (Dashboard)
  todaySummary: {
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    onLeave: 0,
    medicalLeave: 0,
    workFromHome: 0,
    totalWorkHours: 0,
    avgWorkHours: 0,
    totalEmployees: 0,
    employeesWithRecords: 0,
    employeesWithoutRecords: 0,
    date: null,
    records: [],
  },
  todayLoading: false,
  todayError: null,

  // My Attendance (Current User)
  myAttendance: {
    employeeId: null,
    employeeName: '',
    employeeCode: '',
    department: '',
    role: '',
    records: [],
    summary: {
      present: 0,
      absent: 0,
      late: 0,
      halfDay: 0,
      onLeave: 0,
      medicalLeave: 0,
      totalRecords: 0,
      totalWorkHours: 0,
      avgWorkHours: 0,
    },
    count: 0,
  },
  myAttendanceLoading: false,
  myAttendanceError: null,

  // Employee Summary (Manager/HR View)
  employeeSummary: {
    employeeId: null,
    employeeName: '',
    employeeCode: '',
    department: '',
    role: '',
    records: [],
    summary: {
      present: 0,
      absent: 0,
      late: 0,
      halfDay: 0,
      onLeave: 0,
      medicalLeave: 0,
      totalRecords: 0,
      totalWorkHours: 0,
      avgWorkHours: 0,
    },
    count: 0,
    totalEmployees: 0,
  },
  employeeSummaryLoading: false,
  employeeSummaryError: null,

  // Live Attendance
  liveAttendance: [],
  liveLoading: false,
  liveError: null,

  // Edit punch/status state
  editLoading: false,
  editError: null,

  // Filters for My Attendance
  myFilters: {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: '',
  },

  // Filters for Employee Summary
  summaryFilters: {
    employeeId: null,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: '',
  },
};

// ─── helper: sync an updated record into all record arrays ───────────────────
function syncRecord(state, updatedRecord) {
  if (!updatedRecord?.id) return;

  const todayIdx = state.todaySummary.records.findIndex(r => r.id === updatedRecord.id);
  if (todayIdx !== -1) state.todaySummary.records[todayIdx] = updatedRecord;

  const myIdx = state.myAttendance.records.findIndex(r => r.id === updatedRecord.id);
  if (myIdx !== -1) state.myAttendance.records[myIdx] = updatedRecord;

  const empIdx = state.employeeSummary.records.findIndex(r => r.id === updatedRecord.id);
  if (empIdx !== -1) state.employeeSummary.records[empIdx] = updatedRecord;
}
// ─────────────────────────────────────────────────────────────────────────────

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    setMyFilters: (state, action) => {
      state.myFilters = { ...state.myFilters, ...action.payload };
    },
    clearMyFilters: (state) => {
      state.myFilters = {
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        status: '',
      };
    },
    setSummaryFilters: (state, action) => {
      state.summaryFilters = { ...state.summaryFilters, ...action.payload };
    },
    clearSummaryFilters: (state) => {
      state.summaryFilters = {
        employeeId: null,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        status: '',
      };
    },
    clearEmployeeSummary: (state) => {
      state.employeeSummary = initialState.employeeSummary;
      state.employeeSummaryError = null;
    },
    clearEditError: (state) => {
      state.editError = null;
    },
    // Optimistic status update (call before dispatching updateAttendanceStatus)
    updateLocalStatus: (state, action) => {
      const { recordId, newStatus } = action.payload;
      ['todaySummary', 'myAttendance', 'employeeSummary'].forEach(key => {
        const rec = state[key].records.find(r => r.id === recordId);
        if (rec) rec.status = newStatus;
      });
    },
  },
  extraReducers: (builder) => {
    // ── TODAY'S SUMMARY ──────────────────────────────────────────────────────
    builder
      .addCase(fetchTodaySummary.pending,    (state) => { state.todayLoading = true;  state.todayError = null; })
      .addCase(fetchTodaySummary.fulfilled,  (state, action) => { state.todayLoading = false; state.todaySummary = action.payload; })
      .addCase(fetchTodaySummary.rejected,   (state, action) => { state.todayLoading = false; state.todayError = action.payload || 'Failed to fetch today\'s summary'; });

    // ── MY ATTENDANCE ────────────────────────────────────────────────────────
    builder
      .addCase(fetchMyAttendance.pending,    (state) => { state.myAttendanceLoading = true;  state.myAttendanceError = null; })
      .addCase(fetchMyAttendance.fulfilled,  (state, action) => { state.myAttendanceLoading = false; state.myAttendance = action.payload; })
      .addCase(fetchMyAttendance.rejected,   (state, action) => { state.myAttendanceLoading = false; state.myAttendanceError = action.payload || 'Failed to fetch your attendance'; });

    // ── EMPLOYEE SUMMARY ─────────────────────────────────────────────────────
    builder
      .addCase(fetchEmployeeSummary.pending,   (state) => { state.employeeSummaryLoading = true;  state.employeeSummaryError = null; })
      .addCase(fetchEmployeeSummary.fulfilled, (state, action) => { state.employeeSummaryLoading = false; state.employeeSummary = action.payload; })
      .addCase(fetchEmployeeSummary.rejected,  (state, action) => { state.employeeSummaryLoading = false; state.employeeSummaryError = action.payload || 'Failed to fetch employee summary'; });

    // ── LIVE ATTENDANCE ──────────────────────────────────────────────────────
    builder
      .addCase(fetchLiveAttendance.pending,   (state) => { state.liveLoading = true;  state.liveError = null; })
      .addCase(fetchLiveAttendance.fulfilled, (state, action) => { state.liveLoading = false; state.liveAttendance = action.payload; })
      .addCase(fetchLiveAttendance.rejected,  (state, action) => { state.liveLoading = false; state.liveError = action.payload || 'Failed to fetch live attendance'; });

    // ── UPDATE STATUS ────────────────────────────────────────────────────────
    builder
      .addCase(updateAttendanceStatus.pending,  (state) => { state.editLoading = true;  state.editError = null; })
      .addCase(updateAttendanceStatus.fulfilled, (state, action) => {
        state.editLoading = false;
        syncRecord(state, action.payload);
      })
      .addCase(updateAttendanceStatus.rejected,  (state, action) => {
        state.editLoading = false;
        state.editError = action.payload || 'Failed to update status';
        // Revert optimistic update
        const { recordId, oldStatus } = action.meta?.arg || {};
        if (recordId && oldStatus) {
          ['todaySummary', 'myAttendance', 'employeeSummary'].forEach(key => {
            const rec = state[key].records.find(r => r.id === recordId);
            if (rec) rec.status = oldStatus;
          });
        }
      });

    // ── UPDATE PUNCHES ───────────────────────────────────────────────────────
    builder
      .addCase(updateAttendancePunches.pending,  (state) => { state.editLoading = true;  state.editError = null; })
      .addCase(updateAttendancePunches.fulfilled, (state, action) => {
        state.editLoading = false;
        syncRecord(state, action.payload);
      })
      .addCase(updateAttendancePunches.rejected,  (state, action) => {
        state.editLoading = false;
        state.editError = action.payload || 'Failed to update punch times';
      });

    // ── CREATE ───────────────────────────────────────────────────────────────
    builder
      .addCase(createAttendance.fulfilled, (state, action) => {
        const newRecord = action.payload;
        const today = new Date().toISOString().split('T')[0];
        if (newRecord.date === today) state.todaySummary.records.push(newRecord);
      });

    // ── DELETE ───────────────────────────────────────────────────────────────
    builder
      .addCase(deleteAttendance.fulfilled, (state, action) => {
        const id = action.payload;
        state.todaySummary.records    = state.todaySummary.records.filter(r => r.id !== id);
        state.myAttendance.records    = state.myAttendance.records.filter(r => r.id !== id);
        state.employeeSummary.records = state.employeeSummary.records.filter(r => r.id !== id);
      });
  },
});

export const {
  setMyFilters,
  clearMyFilters,
  setSummaryFilters,
  clearSummaryFilters,
  clearEmployeeSummary,
  clearEditError,
  updateLocalStatus,
} = attendanceSlice.actions;

export default attendanceSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectTodaySummary         = (state) => state.attendance.todaySummary;
export const selectTodayLoading         = (state) => state.attendance.todayLoading;
export const selectTodayError           = (state) => state.attendance.todayError;

export const selectMyAttendance         = (state) => state.attendance.myAttendance;
export const selectMyAttendanceLoading  = (state) => state.attendance.myAttendanceLoading;
export const selectMyAttendanceError    = (state) => state.attendance.myAttendanceError;

export const selectEmployeeSummary      = (state) => state.attendance.employeeSummary;
export const selectEmployeeSummaryLoading = (state) => state.attendance.employeeSummaryLoading;
export const selectEmployeeSummaryError = (state) => state.attendance.employeeSummaryError;

export const selectLiveAttendance       = (state) => state.attendance.liveAttendance;
export const selectLiveLoading          = (state) => state.attendance.liveLoading;

export const selectEditLoading          = (state) => state.attendance.editLoading;
export const selectEditError            = (state) => state.attendance.editError;